import { createContext, useContext, ReactNode, useState, useRef, useEffect, useCallback } from "react";

export interface WebsiteContextType {
  // state
  isLoading: boolean;
  loadingPercent: number;

  setPageTitle: (title: string) => void;
  pageTitle: string;
  setPageIcon: (icon: React.ReactNode | undefined) => void;
  pageIcon: React.ReactNode | undefined;

  // navigation
  currentURL: string;
  navigateToURL: (url: string) => void;
  canGoBack: boolean;
  canGoForward: boolean;
  goBack: () => void;
  goForward: () => void;

  // instance tracking
  instanceId: string;
}

const WebsiteContext = createContext<WebsiteContextType | undefined>(undefined);

export function useWebsite() {
  const context = useContext(WebsiteContext);
  if (!context) {
    throw new Error("useWebsite must be used within a WebsiteProvider");
  }
  return context;
}


export default function WebsiteProvider({ children, instanceId: providedInstanceId, initialURL }: { children: ReactNode; instanceId?: string; initialURL?: string }) {
  const [instanceId] = useState(() => providedInstanceId || `browser-${Math.random().toString(36).substr(2, 9)}`);

  const [isLoading, setIsLoading] = useState(false);
  const [loadingPercent, setLoadingPercent] = useState(0);

  const loadingIntervalRef = useRef<number | null>(null);
  const loadingStartRef = useRef<number | null>(null);
  const loadingDurationRef = useRef<number>(0);
  const loadingProfileRef = useRef<'front' | 'back'>('front');

  // cleanup on unmount
  useEffect(() => {
    // startLoading(); // trigger loading on mount so opening the browser shows loading

    return () => {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
        loadingIntervalRef.current = null;
      }
    };
  }, []);

  // start loading simulation
  const startLoading = useCallback(() => {
    if (loadingIntervalRef.current) {
      clearInterval(loadingIntervalRef.current);
      loadingIntervalRef.current = null;
    }

    setIsLoading(true);
    setLoadingPercent(0);

    // pick randomized duration and a profile (fast start or fast end)
    const MIN_DURATION = 500; // ms
    const MAX_DURATION = 2000; // ms
    const duration = Math.floor(Math.random() * (MAX_DURATION - MIN_DURATION + 1)) + MIN_DURATION;
    loadingDurationRef.current = duration;
    loadingStartRef.current = (typeof performance !== 'undefined') ? performance.now() : Date.now();
    loadingProfileRef.current = Math.random() < 0.5 ? 'front' : 'back';

    loadingIntervalRef.current = window.setInterval(() => {
      const start = loadingStartRef.current || 0;
      const now = (typeof performance !== 'undefined') ? performance.now() : Date.now();
      const elapsed = Math.max(0, now - start);
      let t = Math.min(1, elapsed / loadingDurationRef.current);

      // apply profile easing: front => fast start, slow end; back => slow start, fast end
      if (loadingProfileRef.current === 'front') {
        // ease out quad
        t = 1 - Math.pow(1 - t, 2);
      } else {
        // ease in quad
        t = Math.pow(t, 2);
      }

      // small random jitter so the progress isn't perfectly uniform
      const jitter = (Math.random() - 0.5) * 0.04; // +/- 2%
      t = Math.min(1, Math.max(0, t + jitter));

      const target = Math.floor(t * 100);

      setLoadingPercent((prev) => {
        // finish loading
        if (target >= 100 || prev >= 100 || elapsed >= loadingDurationRef.current) {
          if (loadingIntervalRef.current) {
            clearInterval(loadingIntervalRef.current);
            loadingIntervalRef.current = null;
          }
          setIsLoading(false);
          return 100;
        }

        // ensure monotonic increase
        return Math.max(prev, target);
      });
    }, 100);
  }, []);

  const [pageTitle, setPageTitle] = useState("Home");
  const [pageIcon, setPageIcon] = useState<React.ReactNode | undefined>(undefined);

  const [currentURL, setCurrentURL] = useState(initialURL || "http://home.dom");
  const [, setHistoryStack] = useState<string[]>([initialURL || "http://home.dom"]);

  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);


  const navigateToURL = useCallback((url: string) => {
    startLoading();

    setPageTitle(url);
    setPageIcon(undefined);

    // update history
    setHistoryStack((prev) => {
      const newHistory = prev.slice(0, prev.indexOf(currentURL) + 1);
      newHistory.push(url);
      setCanGoBack(newHistory.length > 1);
      setCanGoForward(false);
      return newHistory;
    });

    setCurrentURL(url);
  }, [currentURL, startLoading]);

  const goBack = () => {
    setHistoryStack((prev) => {
      const currentIndex = prev.indexOf(currentURL);
      if (currentIndex > 0) {
        const newURL = prev[currentIndex - 1];

        setCurrentURL(newURL);
        setPageTitle(newURL);
        setPageIcon(undefined);
        startLoading();
        setCanGoBack(currentIndex - 1 > 0);
        setCanGoForward(true);
      }
      return prev;
    });
  };

  const goForward = () => {
    setHistoryStack((prev) => {
      const currentIndex = prev.indexOf(currentURL);
      if (currentIndex < prev.length - 1) {
        const newURL = prev[currentIndex + 1];

        setCurrentURL(newURL);
        setPageTitle(newURL);
        setPageIcon(undefined);
        startLoading();
        setCanGoBack(true);
        setCanGoForward(currentIndex + 1 < prev.length - 1);
      }
      return prev;
    });
  };

  const value: WebsiteContextType = {
    isLoading,
    loadingPercent,
    pageTitle,
    setPageTitle,
    pageIcon,
    setPageIcon,
    currentURL,
    navigateToURL,
    canGoBack,
    canGoForward,
    goBack,
    goForward,
    instanceId,
  };

  return <WebsiteContext.Provider value={value}>{children}</WebsiteContext.Provider>;
}
